import { ChevronRight } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import type {
  Command,
  CommandHandover,
  ManifestObject,
  RecordNotice,
  RecordView,
} from "@shared-core/domain/object-ui/contract";
import type {
  PresentedChild,
  PresentedChildRow,
  PresentedFact,
  PresentedRelated,
} from "@shared-core/domain/object-ui/record";
import {
  useObjectRecord,
  useRecordOpened,
  useRelatedCollectionCommands,
  useRelatedRows,
  useWorkThrough,
} from "@shared-core/domain/object-ui/hooks";
import { railOf } from "@shared-core/domain/object-ui/lifecycle";
import { parseListQuery } from "@shared-core/domain/object-ui/list-query";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import {
  headerFactsOf,
  presentRecord,
} from "@shared-core/domain/object-ui/record";
import { recordPrefillOf } from "@shared-core/domain/object-ui/related-prefill";
import {
  declaredMainOf,
  headerMainOf,
  ownerHasSpoken,
} from "@shared-core/domain/object-ui/row-commands";
import {
  rowSettingsOf,
  settingsOf,
  type RecordSettings,
  type Setting,
} from "@shared-core/domain/object-ui/settings";
import { blockedLayoutOf } from "@shared-core/domain/object-ui/shared-reasons";
import {
  parseWorkThrough,
  workThroughSearch,
  type WorkThroughParams,
} from "@shared-core/domain/object-ui/work-through";
import { paths } from "@shared-core/routing/routes";
import { BULK_TARGETS_FIELD } from "@shared-core/domain/object-ui/contract";
import { LifecycleRail } from "../../app/components/patterns/LifecycleRail";
import { DetailValue } from "../../app/components/patterns/DetailValue";
import { FileLinks } from "../../app/components/patterns/FileLinks";
import { FormattedText } from "../../app/components/patterns/FormattedText";
import {
  ObjectHeader,
  type KeyFact,
} from "../../app/components/patterns/ObjectHeader";
import { SettingFact } from "../../app/components/patterns/SettingFact";
import type { KnownEntities } from "../../app/components/patterns/command-shell/types";
import { ChildGrid } from "../../app/components/patterns/ChildGrid";
import { ChildStack } from "../../app/components/patterns/ChildStack";
import { ErrorPanel } from "../../app/components/patterns/ErrorPanel";
import { MoreCommands } from "../../app/components/patterns/MoreCommands";
import { RefLinks } from "../../app/components/patterns/RefLinks";
import { StateBadge } from "../../app/components/patterns/StateBadge";
import { RowCommands } from "../../app/components/patterns/RowCommands";
import { StackedRows } from "../../app/components/patterns/StackedRows";
import { Button } from "../../app/components/primitives/button";
import { Card, CardContent } from "../../app/components/primitives/card";
import { Skeleton } from "../../app/components/primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import {
  COMMAND_WIDGETS,
  RECORD_LEAD_SECTIONS,
  RECORD_SECTIONS,
} from "../../app/object-ui-registry";
import { NotFoundPage } from "../NotFoundPage";
import {
  collectionCommandOpening,
  useCommandHost,
  type OpenCommand,
} from "../../app/useCommandHost";
import { useIsPhone } from "../../app/useIsPhone";
import { ObjectSwitchedOffPage } from "./ObjectSwitchedOffPage";
import { RecordTimeline } from "./RecordTimeline";

/**
 * The record template (04_frontend_spec.md §6.6), the same for every
 * object: header with the available commands (a disabled one shows why),
 * the lifecycle rail where the object has one, its children with their row
 * commands (and a Total row where the child declares totals), any custom
 * sections registered for the object, related lists and the timeline. Facts
 * the object groups draw on lines of their own; a fact or cell with a detail
 * shows it on hover and focus (docs/object-contract.md, "Laying out a record"). When working through a list,
 * a bar steps to the previous and next record.
 */
export function ObjectRecordPage() {
  const { objectId = "", recordKey = "" } = useParams();
  const index = useObjectManifest();
  if (!index.isObjectKnown(objectId) || !index.objectOf(objectId).record)
    return <NotFoundPage />;
  if (!index.isObjectEnabled(objectId))
    return <ObjectSwitchedOffPage object={index.objectOf(objectId)} />;
  return (
    <ObjectRecord
      key={`${objectId}/${recordKey}`}
      object={index.objectOf(objectId)}
      recordKey={recordKey}
    />
  );
}

/**
 * A commit that handed the person on here (`CommandHandover`) names one of this record's commands: once the record
 * has loaded, that command opens with the handed-over starting values, once. If the record does not offer it, or it
 * cannot run now, the record simply shows (its header says why).
 */
function useHandover(
  object: ManifestObject,
  recordKey: string,
  record: RecordView | null,
  openCommand: (open: OpenCommand) => void,
) {
  const index = useObjectManifest();
  const location = useLocation();
  const navigate = useNavigate();
  const handled = useRef(false);
  const handover =
    (location.state as { handover?: CommandHandover } | null)?.handover ?? null;
  useEffect(() => {
    if (!handover || !record || handled.current) return;
    if (
      handover.record.object !== object.id ||
      handover.record.id !== recordKey
    )
      return;
    handled.current = true;
    // Read once: a reload or Back does not open it again.
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
    const presented = presentRecord(index, object, record);
    const command = presented.commands.find((c) => c.name === handover.command);
    const label = index.commandOf(handover.command).label;
    if (!command) {
      toast.info(`${label} can't be used on ${presented.heading} now.`);
      return;
    }
    if (!command.enabled) {
      toast.info(
        command.reason
          ? `${label} can't run on ${presented.heading}. ${command.reason}`
          : `${label} can't run on ${presented.heading} now.`,
      );
      return;
    }
    openCommand({
      endpoint: {
        command: index.commandOf(command.name),
        href: command.href,
        dryRunHref: command.dry_run_href,
      },
      context: presented.heading,
      initialBody: handover.body,
    });
  }, [
    handover,
    record,
    object,
    recordKey,
    index,
    navigate,
    location.pathname,
    location.search,
    openCommand,
  ]);
}

function CommandButton({
  command,
  isPrimary,
  onRun,
}: {
  command: Command;
  isPrimary: boolean;
  onRun: () => void;
}) {
  return (
    <div className="flex max-w-56 flex-col gap-0.5">
      {/* A long label is cut at the button's width; the whole of it is the tooltip. */}
      <Button
        size="sm"
        variant={isPrimary ? "default" : "outline"}
        disabled={!command.enabled}
        onClick={onRun}
        title={command.label}
        className="min-w-0 max-w-full max-sm:h-10"
      >
        <span className="truncate">{command.label}</span>
      </Button>
      {!command.enabled && command.reason && (
        <span className="text-xs text-amber-700 dark:text-amber-400">
          {command.reason}
        </span>
      )}
    </div>
  );
}

function ObjectRecord({
  object,
  recordKey,
}: {
  object: ManifestObject;
  recordKey: string;
}) {
  const index = useObjectManifest();
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;
  const walk = parseWorkThrough(search);
  const read = useObjectRecord(object, recordKey);
  useRecordOpened(object, recordKey, read.status === "ready");
  const { openCommand, shell } = useCommandHost();
  const isPhone = useIsPhone();
  useHandover(
    object,
    recordKey,
    read.status === "ready" ? read.value : null,
    openCommand,
  );

  if (read.status === "loading")
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  if (read.status === "error") {
    if (read.httpStatus === 404) return <NotFoundPage />;
    return read.httpStatus === 403 ? (
      <p className="text-sm text-muted-foreground">
        Your role can't open this {object.label.toLowerCase()}.
      </p>
    ) : (
      <ErrorPanel
        message={`Couldn't load ${object.label.toLowerCase()} ${recordKey}.`}
        onRetry={read.reload}
      />
    );
  }

  const record = read.value;
  const presented = presentRecord(index, object, record);
  const context = presented.heading;

  // A command that declares the facts it edits is shown beside them, not in the header.
  const settings = settingsOf(index, presented.facts, presented.commands);
  const leadSections = RECORD_LEAD_SECTIONS[object.id] ?? [];
  const headerCommands = presented.commands.filter(
    (c) => !settings.shownAsSettings.has(c.name),
  );
  const headerFacts = headerFactsOf(
    presented.facts,
    (field) => settings.byField.has(field),
    presented.factGroups,
  );

  const run = (
    command: Command,
    rowLabel?: string,
    startingBody?: Record<string, unknown>,
    knownEntities?: KnownEntities,
  ) => {
    const declared = index.commandOf(command.name);
    const isCollection = declared.target.startsWith("collection:");
    // A collection command whose entity field names this record's object (Request merchandise quote… on a customer)
    // starts with this record picked: the person chose it by opening the page.
    const prefill = isCollection
      ? recordPrefillOf(declared, object.id, recordKey)
      : null;
    const open: OpenCommand = {
      endpoint: {
        // The owner may word a command for this record (Post as paid by card… for Release payment…): the dialog says so too.
        command:
          command.label === declared.label
            ? declared
            : { ...declared, label: command.label },
        href: command.href,
        dryRunHref: command.dry_run_href,
      },
      context: rowLabel ? `${context} · ${rowLabel}` : context,
      // A bulk command on the record's own object runs with this record as its one target.
      ...(declared.bulk && declared.target === object.id
        ? { initialBody: { [BULK_TARGETS_FIELD]: [recordKey] } }
        : {}),
      // What the command made opens next (New draft quote… on a customer opens the draft quote to build).
      ...(prefill && prefill.lockedPaths.length > 0
        ? {
            initialBody: prefill.body,
            lockedPaths: prefill.lockedPaths,
            opensCreated: true,
          }
        : {}),
      // A setting's switch opens its command already set the way the person turned it.
      ...(startingBody ? { initialBody: startingBody } : {}),
      // A setting's typeahead that grew into this dialog names the person it picked.
      ...(knownEntities ? { knownEntities } : {}),
    };
    openCommand(open);
  };

  /** A section's button: one of the record's own commands, by id. */
  const runByName = (name: string) => {
    const command = presented.commands.find((c) => c.name === name);
    if (command) run(command);
  };

  /** The owner says this setting's command leads: when the setting is empty, it is what holds the record up. */
  const leadsRecord = (setting: Setting) =>
    setting.kind === "edit" && setting.commands[0]?.main === true;

  /** A header fact as drawn: a setting's control, its links, its URL, its detail on hover, or its value. */
  const keyFactOf = (fact: PresentedFact): KeyFact => {
    const setting = settings.byField.get(fact.field);
    return {
      label: fact.label,
      value: setting ? (
        <SettingFact
          label={fact.label}
          value={fact.value}
          detail={fact.detail ?? null}
          isYesNo={typeof fact.raw === "boolean"}
          setting={setting}
          widgets={COMMAND_WIDGETS}
          needsAttention={leadsRecord(setting)}
          onOpen={(command, body, known) =>
            run(command, undefined, body, known)
          }
        />
      ) : fact.refs ? (
        <RefLinks refs={fact.refs} isPath={fact.isPath ?? false} />
      ) : fact.files ? (
        <FileLinks files={fact.files} />
      ) : fact.formatted ? (
        <FormattedText blocks={fact.formatted} />
      ) : fact.href ? (
        <a
          href={fact.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {fact.linkText ?? fact.value}
        </a>
      ) : fact.detail ? (
        <DetailValue value={fact.value} detail={fact.detail} inline={isPhone} />
      ) : (
        fact.value
      ),
    };
  };

  /** The row's commands that are not drawn as a setting in one of its cells. */
  const rowCommandsBeside = (
    row: PresentedChildRow,
    settings: RecordSettings,
  ): Command[] =>
    row.commands.filter((c) => !settings.shownAsSettings.has(c.name));
  const hasRowCommands = (child: PresentedChild) =>
    child.rows.some(
      (row) =>
        rowCommandsBeside(row, rowSettingsOf(index, child.columns, row))
          .length > 0,
    );

  /** A child row's cell as drawn: a setting's control (Optional extra…), its links, its files, its detail, or its value. */
  const cellOf = (
    child: PresentedChild,
    row: PresentedChildRow,
    n: number,
    settings: RecordSettings,
  ) => {
    const column = child.columns[n]!;
    const cell = row.cells[n]!;
    const setting = settings.byField.get(column.field);
    if (setting)
      return (
        // A setting's click never reaches the row, should a row open its record on a click.
        <span onClick={(e) => e.stopPropagation()}>
          <SettingFact
            label={column.label}
            value={cell}
            isYesNo={false}
            setting={setting}
            widgets={COMMAND_WIDGETS}
            needsAttention={leadsRecord(setting)}
            onOpen={(command, body, known) =>
              run(command, row.commandHeading, body, known)
            }
          />
        </span>
      );
    const refs = row.cellRefs[n];
    if (refs) return <RefLinks refs={refs} />;
    const files = row.cellFiles[n];
    if (files) return <FileLinks files={files} />;
    const formatted = row.cellFormatted[n];
    if (formatted) return <FormattedText blocks={formatted} />;
    const detail = row.cellDetails[n];
    if (detail)
      return <DetailValue value={cell} detail={detail} inline={isPhone} />;
    return cell;
  };

  /**
   * A child row's first cell. As on a list, the whole row opens the row's record; a record the cell names (a supplier
   * mapping's supplier) links to its own page, and otherwise the cell links the row's record too.
   */
  const titleCellOf = (
    child: PresentedChild,
    row: PresentedChildRow,
    settings: RecordSettings,
  ) =>
    row.href && !row.cellRefs[0] ? (
      <Link
        to={row.href}
        className="text-primary underline-offset-2 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {row.cells[0]}
      </Link>
    ) : (
      cellOf(child, row, 0, settings)
    );

  const steps = record.steps ?? [];
  // A stacked child with no rows draws nothing: there is no prose to read (an email no actions were proposed from).
  const tableChildren = presented.children.filter(
    (child) => !(child.stack && child.rows.length === 0),
  );
  const listHref = paths.objectList({ objectId: object.id });

  /** A table of the record as a card: its heading with the count, the rows, the Total row and "View all". */
  const childCard = (child: PresentedChild) => (
    <Card key={child.object}>
      <CardContent className="space-y-2 py-4 max-sm:px-4">
        <h2 className="text-base font-semibold">
          {child.label}{" "}
          <span className="text-sm font-normal text-muted-foreground">
            ({child.total})
          </span>
        </h2>
        {child.note ? (
          <p className="text-sm text-muted-foreground">{child.note}</p>
        ) : null}
        {child.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">None yet.</p>
        ) : child.stack ? (
          <ChildStack
            blocks={child.stack}
            isPhone={isPhone}
            declaredMainOf={(commands) =>
              declaredMainOf(index, commands, child.object)
            }
            onRun={run}
          />
        ) : child.grid ? (
          <ChildGrid
            groups={child.grid}
            rowHeading={child.label}
            isPhone={isPhone}
            declaredMainOf={(commands) =>
              declaredMainOf(index, commands, child.object)
            }
            onRun={run}
          />
        ) : isPhone ? (
          <StackedRows
            ariaLabel={child.label}
            className="-mx-3"
            rows={child.rows.map((row) => {
              const settings = rowSettingsOf(index, child.columns, row);
              const commands = rowCommandsBeside(row, settings);
              const href = row.href;
              return {
                key: row.key,
                title: titleCellOf(child, row, settings),
                ...(href ? { onOpen: () => navigate(href) } : {}),
                fields: row.cells.slice(1).map((_, n) => ({
                  label: child.columns[n + 1]!.label,
                  value: cellOf(child, row, n + 1, settings),
                })),
                ...(commands.length > 0
                  ? {
                      actions: (
                        <RowCommands
                          commands={commands}
                          declaredMain={declaredMainOf(
                            index,
                            commands,
                            child.object,
                          )}
                          isPhone
                          onRun={(command) => run(command, row.commandHeading)}
                        />
                      ),
                    }
                  : {}),
              };
            })}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {child.columns.map((column) => (
                    <TableHead key={column.field}>{column.label}</TableHead>
                  ))}
                  {hasRowCommands(child) && (
                    <TableHead>
                      <span className="sr-only">Commands</span>
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {child.rows.map((row) => {
                  const settings = rowSettingsOf(index, child.columns, row);
                  const commands = rowCommandsBeside(row, settings);
                  const href = row.href;
                  return (
                    <TableRow
                      key={row.key}
                      className={href ? "cursor-pointer" : undefined}
                      onClick={href ? () => navigate(href) : undefined}
                    >
                      {row.cells.map((_, n) => (
                        <TableCell
                          key={child.columns[n]!.field}
                          className={
                            n === 0 ? "font-medium tabular-nums" : undefined
                          }
                        >
                          {n === 0
                            ? titleCellOf(child, row, settings)
                            : cellOf(child, row, n, settings)}
                        </TableCell>
                      ))}
                      {hasRowCommands(child) && (
                        <TableCell>
                          {commands.length > 0 && (
                            <RowCommands
                              commands={commands}
                              declaredMain={declaredMainOf(
                                index,
                                commands,
                                child.object,
                              )}
                              isPhone={false}
                              onRun={(command) =>
                                run(command, row.commandHeading)
                              }
                            />
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
              {child.totals && (
                <TableFooter>
                  <TableRow>
                    {child.totals.cells.map((figure, n) => (
                      <TableCell
                        key={child.columns[n]!.field}
                        className="font-semibold tabular-nums"
                      >
                        {n === 0 ? child.totals!.label : figure}
                      </TableCell>
                    ))}
                    {hasRowCommands(child) && <TableCell />}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
        )}
        {isPhone && child.rows.length > 0 && child.totals && (
          <div className="space-y-1 border-t border-border pt-2 text-sm">
            <p className="font-semibold">{child.totals.label}</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              {child.totals.cells.map((figure, n) =>
                figure === null ? null : (
                  <div key={child.columns[n]!.field} className="contents">
                    <dt className="text-muted-foreground">
                      {child.columns[n]!.label}
                    </dt>
                    <dd className="font-semibold tabular-nums">{figure}</dd>
                  </div>
                ),
              )}
            </dl>
          </div>
        )}
        {child.viewAll && (
          <Button asChild variant="outline" size="sm" className="max-sm:h-10">
            <Link to={child.viewAll.href}>{child.viewAll.label}</Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      {walk && (
        <WorkThroughBar object={object} params={walk} recordKey={recordKey} />
      )}
      <ObjectHeader
        crumbs={[
          // A record-only object (a shared communication) has no list to go back to.
          { label: object.plural, to: object.list ? listHref : undefined },
          // A record with no number of its own (an internal id) is named by its title, never its id.
          { label: presented.name },
        ]}
        title={presented.heading}
        pills={
          <>
            {presented.states.map((state) => (
              <StateBadge key={state.label} tone={state.tone}>
                {state.label}
              </StateBadge>
            ))}
          </>
        }
        subtitle={
          presented.stateMeanings.length > 0 && (
            <div className="space-y-0.5 text-sm text-muted-foreground">
              {presented.stateMeanings.map((meaning) => (
                <p key={meaning}>{meaning}</p>
              ))}
            </div>
          )
        }
        facts={headerFacts.line.map(keyFactOf)}
        factGroups={headerFacts.groups.map((group) => ({
          label: group.label,
          facts: group.facts.map(keyFactOf),
        }))}
      >
        {/* A section (an email's The email, Tags) draws its groups under its heading: each a line of short facts, or a
            long text in place. A long text named like its section shows without its label. */}
        {headerFacts.sections.map((section) => (
          <section
            key={section.label}
            aria-label={section.label}
            className="space-y-2 border-t border-border pt-3"
          >
            {section.hasHeading && (
              <h2 className="text-sm font-semibold">{section.label}</h2>
            )}
            {section.groups.flatMap((group, g) => {
              if (group.isTitle)
                return [
                  <p key={g} className="font-semibold">
                    {group.facts.map((fact) => fact.value).join(" · ")}
                  </p>,
                ];
              const short = group.facts.filter((fact) => !fact.isLongText);
              const long = group.facts.filter((fact) => fact.isLongText);
              return [
                ...(short.length > 0
                  ? [
                      <dl
                        key={`${g}-short`}
                        className="flex flex-wrap items-baseline gap-y-1 text-sm"
                      >
                        {short.map(keyFactOf).map((fact, i) => (
                          <div key={fact.label} className="min-w-0">
                            <dt className="inline text-muted-foreground">
                              {fact.label}{" "}
                            </dt>
                            <dd className="inline break-words font-medium text-foreground">
                              {fact.value}
                            </dd>
                            {i < short.length - 1 && (
                              <span
                                aria-hidden="true"
                                className="mx-2 text-muted-foreground"
                              >
                                ·
                              </span>
                            )}
                          </div>
                        ))}
                      </dl>,
                    ]
                  : []),
                ...long.map((fact) => (
                  <div key={fact.field} className="space-y-1">
                    {fact.label !== section.label && (
                      <p className="text-sm text-muted-foreground">
                        {fact.label}
                      </p>
                    )}
                    {fact.formatted ? (
                      <FormattedText blocks={fact.formatted} />
                    ) : (
                      <p className="whitespace-pre-line break-words text-sm">
                        {fact.value}
                      </p>
                    )}
                  </div>
                )),
              ];
            })}
          </section>
        ))}
        {/* A long text (a ticket's Details) is a block of its own under the facts, its line breaks kept. */}
        {headerFacts.blocks.map((fact) => (
          <div
            key={fact.label}
            className="space-y-1 border-t border-border pt-3"
          >
            <p className="text-sm text-muted-foreground">{fact.label}</p>
            {fact.formatted ? (
              <FormattedText blocks={fact.formatted} />
            ) : (
              <p className="whitespace-pre-line break-words text-sm">
                {fact.value}
              </p>
            )}
          </div>
        ))}
        <SeeAlso
          related={presented.related.filter((related) => related.isSecondary)}
        />
        <HeaderCommands
          commands={headerCommands}
          notices={presented.notices}
          related={presented.related}
          declaredMain={declaredMainOf(index, headerCommands, object.id)}
          isPhone={isPhone}
          context={context}
          onRun={(command) => run(command)}
          onOpen={openCommand}
        />
      </ObjectHeader>

      {/* One full-width column (Ben, 20 Oct): the lifecycle, the record's lists, its custom sections, its timeline. */}
      <div className="min-w-0 space-y-4">
        {steps.length > 0 && (
          <Card>
            <CardContent className="py-4 max-sm:px-4">
              <LifecycleRail
                rail={railOf(steps)}
                ariaLabel={`Where ${object.label.toLowerCase()} ${presented.name} is`}
              />
            </CardContent>
          </Card>
        )}
        {leadSections.map(({ Section }, n) => (
          <Section key={n} record={record} />
        ))}
        {tableChildren.map((child) => childCard(child))}
        {(RECORD_SECTIONS[object.id] ?? []).map((Section, n) => (
          <Section key={n} record={record} runCommand={runByName} />
        ))}
        {/* Each primary related list as a table at the foot of the record, like a child's, its first rows with "View
            all"; the secondary ones are links on the header's "See also" line. */}
        {presented.related
          .filter((related) => !related.isSecondary)
          .map((related) => (
            <RelatedSection
              key={related.object}
              related={related}
              childCard={childCard}
            />
          ))}
        {object.record?.timeline_path && (
          <RecordTimeline object={object} recordKey={recordKey} />
        )}
      </div>
      {shell}
    </div>
  );
}

/**
 * One related list as a section of the record, drawn like a child table: "Emails (7)", its first three rows, "View all 7
 * emails →" to the filtered list, "None yet." where it holds none (Ben's R39: a count and a few rows, never a sum or
 * a value). It reads on its own, so the record is on screen before its lists are; a list this person's role cannot see
 * is left out, one that failed to read says so with a retry.
 */
function RelatedSection({
  related,
  childCard,
}: {
  related: PresentedRelated;
  childCard: (child: PresentedChild) => ReactNode;
}) {
  const read = useRelatedRows(related);
  if (read.status === "ready") return childCard(read.value);
  if (read.status === "error" && read.httpStatus === 403) return null;
  return (
    <Card>
      <CardContent className="space-y-2 py-4 max-sm:px-4">
        <h2 className="text-base font-semibold">{related.label}</h2>
        {read.status === "loading" ? (
          <Skeleton
            role="status"
            className="h-16 w-full"
            aria-label={`Loading ${related.label.toLowerCase()}`}
          />
        ) : (
          <p className="text-sm text-destructive" role="alert">
            {`Couldn't load ${related.label.toLowerCase()}. `}
            <button
              type="button"
              onClick={read.reload}
              className="text-primary underline hover:no-underline"
            >
              Retry
            </button>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The record's secondary related lists (`ManifestRelated.secondary`): cross-references a person consults now and then,
 * on one line in the header drawn like a line of facts (Ben, 30 Oct): "See also" as the label, each list a link to its
 * filtered list with its count ("Emails (7)"). Nothing is drawn when there are none.
 */
function SeeAlso({ related }: { related: PresentedRelated[] }) {
  if (related.length === 0) return null;
  return (
    <dl className="flex flex-wrap items-baseline gap-y-1 text-sm">
      <dt className="mr-1 text-muted-foreground">See also</dt>
      {related.map((r, i) => (
        <dd key={r.object} className="inline font-medium">
          <SeeAlsoLink related={r} />
          {i < related.length - 1 && (
            <span
              aria-hidden="true"
              className="mx-2 font-normal text-muted-foreground"
            >
              ·
            </span>
          )}
        </dd>
      ))}
    </dl>
  );
}

/** One "See also" link: the list's label and, once read, how many it holds; left out where this role can't see it. */
function SeeAlsoLink({ related }: { related: PresentedRelated }) {
  const read = useRelatedRows(related);
  if (read.status === "error" && read.httpStatus === 403) return null;
  const count = read.status === "ready" ? ` (${read.value.total})` : "";
  return (
    <Link
      to={related.href}
      className="text-primary underline-offset-2 hover:underline"
    >
      {`${related.label}${count}`}
    </Link>
  );
}

/**
 * The record's own header commands with the ones that create a related record (Add purchase invoice… on a purchase
 * order, Add note… on a lead): those come from the related objects' collection commands and sit after the record's
 * own, opened with this record's params already in. A command the record's header already has is not drawn twice.
 */
function HeaderCommands({
  commands,
  notices,
  related,
  declaredMain,
  isPhone,
  context,
  onRun,
  onOpen,
}: {
  commands: readonly Command[];
  notices: readonly RecordNotice[];
  related: readonly PresentedRelated[];
  declaredMain: string | null;
  isPhone: boolean;
  context: string;
  onRun: (command: Command) => void;
  onOpen: (open: OpenCommand) => void;
}) {
  const index = useObjectManifest();
  const own = new Set(commands.map((c) => c.name));
  const offered = useRelatedCollectionCommands(related).filter(
    ({ command }) => !own.has(command.name),
  );
  if (commands.length === 0 && offered.length === 0) return null;
  const relatedOf = new Map(
    offered.map((o) => [o.command.name, o.related] as const),
  );
  return (
    <RecordCommands
      commands={commands}
      notices={notices}
      alongside={offered.map((o) => o.command)}
      declaredMain={declaredMain}
      isPhone={isPhone}
      onRun={(command) => {
        const list = relatedOf.get(command.name);
        if (!list) return onRun(command);
        onOpen(
          collectionCommandOpening(index, command, {
            context: `${context} · ${list.label}`,
            params: list.params,
          }),
        );
      }}
    />
  );
}

/** The record a notice names, after its sentence; nothing where it names none or the record has no page here. */
function NoticeLink({ link }: { link: RecordNotice["link"] }) {
  const index = useObjectManifest();
  const href = link ? index.hrefOf(link.record) : null;
  if (!link || !href) return null;
  return (
    <>
      {" "}
      <Link
        to={href}
        className="text-primary underline-offset-2 hover:underline"
      >
        {link.label}
      </Link>
    </>
  );
}

/**
 * The record's commands: what can run now as buttons, its main command first
 * and primary only while it can run; the first that cannot run said above the
 * buttons with its reason; the rest folded under "Not available now", each
 * with its reason beside it. A reason that blocks several commands at once is
 * said once, not beside each of them (`blockedLayoutOf`). Commands that create a
 * related record come after the record's own and never lead.
 */
function RecordCommands({
  commands,
  notices,
  alongside,
  declaredMain,
  isPhone,
  onRun,
}: {
  commands: readonly Command[];
  /** Facts that change what the main command does without blocking it, said above the blocked line and the buttons. */
  notices: readonly RecordNotice[];
  /** Commands that create a related record, after the record's own; none of them is primary. */
  alongside: readonly Command[];
  /**
   * The command the object declares as its main one (`main: true`); without one, its first command is. The owning
   * module may override it per record, or say none leads (`Command.main`).
   */
  declaredMain: string | null;
  isPhone: boolean;
  onRun: (command: Command) => void;
}) {
  // The main command is primary only while it can run. Unlike a row's, it may be destructive (Release…, Publish…):
  // every header command that can run is a button anyway, and the main one is the record's next step.
  const primary = headerMainOf(commands, declaredMain);
  const { said, folded, sharedReasons, foldedShared } = blockedLayoutOf([
    ...commands,
    ...alongside,
  ]);
  const available = [
    ...(primary ? [primary] : []),
    ...commands.filter((c) => c.enabled && c !== primary),
    ...alongside.filter((c) => c.enabled),
  ];
  // A phone shows the main command (or the first that can run) and puts the rest behind More…. When the owner has
  // said none leads there is no main command to show: every command that can run is behind More….
  const ownerSaidNoneLeads = !primary && ownerHasSpoken(commands);
  const shown = isPhone
    ? available.slice(0, ownerSaidNoneLeads ? 0 : 1)
    : available;
  const more = isPhone ? available.slice(shown.length) : [];
  return (
    <div className="space-y-2 border-t border-border pt-3">
      {notices.length > 0 && (
        <ul aria-label="Notices" className="space-y-1 text-sm">
          {notices.map((notice) => (
            <li
              key={notice.text}
              className={
                notice.tone === "warn"
                  ? "text-amber-700 dark:text-amber-400"
                  : "text-muted-foreground"
              }
            >
              {notice.text}
              <NoticeLink link={notice.link} />
            </li>
          ))}
        </ul>
      )}
      {said && (
        <p className="text-sm">
          {said.label && <span className="font-medium">{said.label} </span>}
          <span className="text-amber-700 dark:text-amber-400">
            {said.reason}
          </span>
        </p>
      )}
      {available.length > 0 && (
        <div className="flex flex-wrap items-start gap-2">
          {shown.map((command) => (
            <CommandButton
              key={command.name}
              command={command}
              isPrimary={command === primary}
              onRun={() => onRun(command)}
            />
          ))}
          {more.length > 0 && <MoreCommands commands={more} onRun={onRun} />}
        </div>
      )}
      {folded.length > 0 && (
        <details className="group text-sm">
          <summary className="flex cursor-pointer list-none items-center gap-1 text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden">
            <ChevronRight
              className="size-4 transition-transform group-open:rotate-90"
              aria-hidden
            />
            Not available now ({folded.length})
          </summary>
          <div className="mt-2 space-y-1.5 pl-5">
            {foldedShared.map((reason) => (
              <p key={reason} className="text-amber-700 dark:text-amber-400">
                {reason}
              </p>
            ))}
            <ul className="space-y-1.5">
              {folded.map((command) => (
                <li
                  key={command.name}
                  className="grid gap-x-3 sm:grid-cols-[12rem_minmax(0,1fr)]"
                >
                  <span className="font-medium text-muted-foreground">
                    {command.label}
                  </span>
                  {/* A reason said once above is not said again beside each command it blocks. */}
                  {!(
                    command.reason && sharedReasons.includes(command.reason)
                  ) && (
                    <span className="text-muted-foreground">
                      {command.reason ?? "Not available now."}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}
    </div>
  );
}
/** "To place · 2 of 7", with Previous, Next and Stop (brief journey 1). */
function WorkThroughBar({
  object,
  params,
  recordKey,
}: {
  object: ManifestObject;
  params: WorkThroughParams;
  recordKey: string;
}) {
  const navigate = useNavigate();
  const read = useWorkThrough(object, params, recordKey);
  const list = object.list!;
  const query = parseListQuery(params.through, list);
  const queue = list.queues.find((q) => q.id === query.queue);
  const listHref = paths.objectList({
    objectId: object.id,
    search: params.through,
  });
  const open = (key: string, at: number) =>
    navigate(
      paths.objectRecord({
        objectId: object.id,
        recordKey: key,
        search: workThroughSearch({ through: params.through, at }),
      }),
    );
  const state = read.status === "ready" ? read.value : null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
      <span role="status">
        <span className="font-medium">{queue?.label ?? object.plural}</span>
        {state?.kind === "empty"
          ? " · nothing left in this list"
          : state?.kind === "walking"
            ? state.isInList
              ? ` · ${state.position} of ${state.total}`
              : ` · done here · ${state.total} left`
            : ""}
      </span>
      <span className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={state?.kind !== "walking" || !state.previous}
          onClick={() =>
            state?.kind === "walking" &&
            state.previous &&
            open(state.previous.key, state.previous.at)
          }
        >
          ← Previous
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={state?.kind !== "walking" || !state.next}
          onClick={() =>
            state?.kind === "walking" &&
            state.next &&
            open(state.next.key, state.next.at)
          }
        >
          Next →
        </Button>
        <Button size="sm" variant="outline" asChild>
          <Link to={listHref}>Stop</Link>
        </Button>
      </span>
    </div>
  );
}
